import type { ProductTileData } from '../../../utils/types/product'
import type { BLOCK_TYPE } from '../enums'
import type { ReferenceId } from '../ids'
import type { ContextParameterReference } from '../references'

import type { IBlock, BlockBaseSettings } from './base'
import type {
  CTA_OPTION,
  RECURRENCE_UNIT,
  VariablePriceReference
} from './productsBlock'

export enum ProductRecommendationSource {
  /**
   * Use a contract ID (in context parameters) to fetch recommendations
   */
  CONTRACT = 'contract',
  /**
   * Use recommendation IDs to fetch recommendations
   */
  RECOMMENDATIONS = 'recommendations'
}

export interface ContractReference extends ContextParameterReference {
  meaning: 'source'
}

export type ProductRecommendationsBlockBaseSettings = {
  /**
   * Choice of "call to action" labels for the button on the product tile.
   */
  ctaOption: CTA_OPTION
  /**
   * Weather to display trailing zeros in the price.
   * Assuming a price of $10, when true the price will be $10.00, otherwise $10
   */
  showTrailingDecimalZeros: boolean
  /**
   * Weather to show a recurring total or a fixed one-time price
   */
  showRecurringTotal: boolean
  /**
   * When showRecurringTotal is true, the interval is defined with recurrenceUnit.
   */
  recurrenceUnit?: RECURRENCE_UNIT
  /**
   * Maximum number of product features to display.
   */
  featuresLimit: number
}

export interface ContractBasedProductRecommendationsBlock extends IBlock {
  type: BLOCK_TYPE.PRODUCT_RECOMMENDATIONS
  settings: BlockBaseSettings &
    ProductRecommendationsBlockBaseSettings & {
      source: ProductRecommendationSource.CONTRACT
    }
  /**
   * Reference to Contract parameter used to fetch recommendations based on contract
   */
  references: Record<ReferenceId, ContractReference | VariablePriceReference>
}

export interface EntityBasedProductRecommendationsBlock extends IBlock {
  type: BLOCK_TYPE.PRODUCT_RECOMMENDATIONS
  settings: BlockBaseSettings &
    ProductRecommendationsBlockBaseSettings & {
      source: ProductRecommendationSource.RECOMMENDATIONS
      /**
       * Product recommendation IDs (entity IDs) to fetch recommendations
       */
      recommendationIds?: string[]
    }
  references: Record<ReferenceId, VariablePriceReference>
}

export type ProductRecommendationsBlock =
  | ContractBasedProductRecommendationsBlock
  | EntityBasedProductRecommendationsBlock

/**
 * Value produced by Product Recommendations blocks.
 * It may either be a single selected product tile or an array of product tiles.
 */
export type ProductRecommendationsBlockValue =
  | ProductTileData
  | ProductTileData[]
  | null
